import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { hydrateSnapshots } from "@/features/authority";
import { QuestionType } from "@/generated/graphql";
import { getItem, intParser } from "@/utils/local-storage";

import { mapAnswerBlocksToPageInfo } from "../utils/answer-blocks";

export interface AnswerSliceState {
  /** Ordered list of all answer IDs in the assessment. */
  pages: PageInfo[];
  /** Current page index of the `pages` array. */
  currentPageIndex: number;
  /** Whether the device check page is visible/used. */
  hasDeviceCheck: boolean;
  /** Whether the device check has been completed successfully. */
  deviceCheckCompleted: boolean;
}

/** Different kinds of pages in Multi-Format */
export type PageInfo =
  | {
      page: "answer";
      answerId: string;
      referencesAnswerId?: string;
      questionType?: QuestionType;
      parentAnswerId?: string;
      confirm?: boolean;
    }
  | { page: "agreement" }
  | { page: "overview"; blockId?: string }
  | { page: "device-check" };

const initialState: AnswerSliceState = {
  currentPageIndex: 0,
  pages: [],
  hasDeviceCheck: false,
  deviceCheckCompleted: false,
};

export const answerSlice = createSlice({
  name: "answer",
  initialState: initialState,
  reducers: {
    /** Go to the previous page in the loaded page list. */
    goPrevPage: (state) => {
      const currIndex = state.currentPageIndex;
      const prevIndex = currIndex - 1;
      if (prevIndex >= 0) {
        state.currentPageIndex = prevIndex;
      }
    },
    /** Go to the next page in the loaded page list. */
    goNextPage: (state) => {
      const currIndex = state.currentPageIndex;
      const nextIndex = currIndex + 1;
      if (nextIndex < state.pages.length) {
        state.currentPageIndex = nextIndex;
      }
    },
    /** Go to a new page. */
    goToPage: (state, action: PayloadAction<PageInfo>) => {
      const index = state.pages.findIndex((pg) => {
        if (action.payload.page === "answer") {
          return (
            pg.page === "answer" && pg.answerId === action.payload.answerId
          );
        }
        return pg.page === action.payload.page;
      });
      if (index >= 0) {
        state.currentPageIndex = index;
      }
    },
    /** Go to the initial page. */
    goToInitialPage: (state) => {
      // The initial page is always at index 0
      // This is usually the overview page, but could be the device check etc.
      state.currentPageIndex = 0;
    },
    /** Set device check completed */
    setDeviceCheckCompleted: (state, action: PayloadAction<boolean>) => {
      state.deviceCheckCompleted = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Setup the answer pages navigation
    builder.addCase(hydrateSnapshots, (state, action) => {
      const { answers, workId, backTrackDisabled } = action.payload;

      const answerPages = mapAnswerBlocksToPageInfo(answers);
      const overviewBlockId = answers.find(
        (block) => block.question?.questionType === QuestionType.Overview
      )?.id;

      const basePages: PageInfo[] = [
        { page: "overview", blockId: overviewBlockId },
        ...answerPages,
        { page: "agreement" },
      ];

      let pageIndex: number | null = null;
      const hasDeviceCheck = answers.some(
        (answerBlock) =>
          answerBlock.question?.questionType === QuestionType.Video
      );

      if (hasDeviceCheck) {
        state.pages = [{ page: "device-check" }, ...basePages];
        state.hasDeviceCheck = true;
      } else {
        state.pages = basePages;
        state.hasDeviceCheck = false;
      }

      if (backTrackDisabled) {
        // When backtracking is disabled, direct the student to the first
        // unconfirmed question so they can't skip ahead or revisit answers.
        // If all questions are confirmed, send them to the agreement page.
        const firstUnconfirmedPage = state.pages.find(
          (answerBlock) => answerBlock.page === "answer" && !answerBlock.confirm
        );
        if (firstUnconfirmedPage) {
          pageIndex = state.pages.indexOf(firstUnconfirmedPage);
        } else {
          // All questions are confirmed
          pageIndex = state.pages.findIndex((p) => p.page === "agreement");
        }
      } else {
        // Load the initial page index from localStorage to provide resume
        // behaviour. Only when backtracking is allowed
        pageIndex = getItem(workId, "currentPageIndex", intParser);
      }
      if (
        pageIndex !== null &&
        pageIndex >= 0 &&
        pageIndex < state.pages.length
      ) {
        state.currentPageIndex = pageIndex;
      } else {
        state.currentPageIndex = 0;
      }
    });
  },
});

export const answerReducer = answerSlice.reducer;
export const {
  goPrevPage,
  goNextPage,
  goToPage,
  goToInitialPage,
  setDeviceCheckCompleted,
} = answerSlice.actions;
